// Reversible URL-safe ID obfuscation for quotation URLs
const SALT = 0x5f3759df;

/**
 * Encodes a numeric database ID into a safe, non-sequential encrypted token (e.g. 5 -> cq_5f3759da0).
 */
export function encodeQuotationId(id) {
  if (id == null || id === '') return '';
  const num = Number(id);
  if (Number.isNaN(num)) {
    try {
      return btoa(String(id))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    } catch {
      return encodeURIComponent(String(id));
    }
  }
  const xor = (num ^ SALT) >>> 0;
  const hex = xor.toString(16);
  const check = ((num * 7 + 13) % 16).toString(16);
  return `cq_${hex}${check}`;
}

/**
 * Decodes an encrypted quotation token back into the raw database ID.
 * Gracefully handles legacy/raw numeric IDs for backward compatibility.
 */
export function decodeQuotationId(token) {
  if (token == null || token === '') return '';
  const str = String(token);

  // If already a raw numeric ID (e.g. "5"), return it directly
  if (/^\d+$/.test(str)) {
    return str;
  }

  // If prefixed with our encrypted format cq_<hex><check>
  if (str.startsWith('cq_')) {
    const body = str.slice(3);
    const hex = body.slice(0, -1);
    const check = body.slice(-1);
    const xor = parseInt(hex, 16);
    if (!Number.isNaN(xor)) {
      const num = (xor ^ SALT) >>> 0;
      const expectedCheck = ((num * 7 + 13) % 16).toString(16);
      if (check === expectedCheck) {
        return String(num);
      }
    }
  }

  // Fallback to standard base64 decoding if previously base64-encoded
  try {
    let b64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const decoded = atob(b64);
    if (decoded && /^\d+$/.test(decoded)) {
      return decoded;
    }
  } catch {
    // ignore
  }

  return str;
}
